import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { Helmet } from "react-helmet-async";

import Sidebar from "../DesignSystem/Sidebar";
import Breadcrumb from "../DesignSystem/Breadcrumb/Breadcrumb";
import { firestore } from "../../backend/firebaseconfig";
import { getPortalSectionConfig } from "./utils/portalSectionConfig";

import "../../css/PortalSections/PortalAccessAuditPage.css";
import { canAccessAML } from "../Tools/AML/Utils/permissions";
import {
  canAccessMyPayments,
  canViewMyPaymentsGroup,
} from "../Tools/MyPayments/access";
import PortalAccessSelectField from "./components/PortalAccessSelectField";

const ROLE_PATHS = [
  {
    role: "Advisers",
    path: ["Authorized Members", "Advisers", "Data"],
  },
  {
    role: "Administrators",
    path: ["Authorized Members", "Administrators", "Data"],
  },
  {
    role: "Staff",
    path: ["Authorized Members", "Staff", "Data"],
  },
  {
    role: "External",
    path: ["Authorized Members", "External", "Data"],
  },
];

const normaliseEmail = (value = "") => String(value).trim().toLowerCase();

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return [value];
};

const getUserName = (user = {}) => {
  return (
    user.name ||
    user.fullName ||
    user.displayName ||
    user.tradingName ||
    user.firstName ||
    user.email ||
    user.userId ||
    "Unknown user"
  );
};

const getPermissionsFromUser = (user = {}) => {
  return [
    ...asArray(user.permissions),
    ...asArray(user.userPermissions),
    ...asArray(user.permission),
    ...asArray(user.access),
    ...(user.canViewPayments === true ? ["Can View Payments"] : []),
    ...(user.canViewGroupPayments === true
      ? ["Can View Group Payments"]
      : []),
  ].filter(Boolean);
};

const getStatusFromUser = (user = {}) => {
  return [
    ...asArray(user.status),
    ...asArray(user.statuses),
    ...asArray(user.category),
    ...asArray(user.categories),
  ].filter(Boolean);
};

const getRelationshipText = (user = {}, role = "") => {
  if (role === "Advisers") {
    return asArray(user.admins).join(", ");
  }

  if (role === "Administrators" || role === "External") {
    return asArray(user.advisers).join(", ");
  }

  return "";
};

const getVisibleSections = (config = {}) => {
  return Object.entries(config)
    .filter(([, section]) => section.show)
    .map(([key, section]) => ({
      key,
      title: section.title,
      route: section.route,
      cards: section.cards || [],
    }));
};

const getConditionBreakdown = ({
  userRole,
  userPermissions = [],
  adviserStatus = [],
  adminStatus = [],
}) => {
  const normaliseList = (list = []) =>
    Array.isArray(list)
      ? list.map((item) => String(item).trim().toLowerCase())
      : [];

  const normaliseValue = (value) => String(value).trim().toLowerCase();

  const normalisedPermissions = normaliseList(userPermissions);
  const normalisedAdviserStatus = normaliseList(adviserStatus);
  const normalisedAdminStatus = normaliseList(adminStatus);

  const hasPermission = (...allowed) =>
    allowed.some((permission) =>
      normalisedPermissions.includes(normaliseValue(permission)),
    );

  const hasAdviserStatus = (...allowed) =>
    allowed.some((status) =>
      normalisedAdviserStatus.includes(normaliseValue(status)),
    );

  const hasAdminStatus = (...allowed) =>
    allowed.some((status) =>
      normalisedAdminStatus.includes(normaliseValue(status)),
    );

  const conditions = [];

  const addCondition = ({
    section,
    tool,
    allowed,
    reason,
    expected,
    actual,
  }) => {
    conditions.push({
      section,
      tool,
      allowed,
      reason,
      expected,
      actual,
    });
  };

  addCondition({
    section: "Tools & Calculators",
    tool: "ATR Profiler",
    allowed:
      userRole === "Staff" || hasAdviserStatus("FP") || hasAdminStatus("NSFP"),
    reason: "Staff OR adviser status FP OR admin status NSFP",
    expected: "Staff / FP / NSFP",
    actual: `${userRole} / ${adviserStatus.join(", ") || "-"} / ${
      adminStatus.join(", ") || "-"
    }`,
  });

  addCondition({
    section: "Tools & Calculators",
    tool: "Cashflow Modeller",
    allowed:
      (userRole === "Staff" &&
        hasPermission("Compliance", "Paraplanning", "Compliance Support")) ||
      hasAdviserStatus("FP", "NAP") ||
      hasAdminStatus("NSFP"),
    reason:
      "Staff with Compliance, Paraplanning or Compliance Support OR adviser status FP/NAP OR admin status NSFP",
    expected: "Staff permissions / FP / NAP / NSFP",
    actual: `${userRole} / permissions: ${
      userPermissions.join(", ") || "-"
    } / adviser: ${adviserStatus.join(", ") || "-"} / admin: ${
      adminStatus.join(", ") || "-"
    }`,
  });

  addCondition({
    section: "Tools & Calculators",
    tool: "DebtSense",
    allowed:
      (userRole === "Staff" &&
        hasPermission("Compliance", "Paraplanning", "Compliance Support")) ||
      hasAdviserStatus("MA", "NAP") ||
      hasAdminStatus("NSMA"),
    reason:
      "Staff with Compliance, Paraplanning or Compliance Support OR adviser status MA/NAP OR admin status NSMA",
    expected: "Staff permissions / MA / NAP / NSMA",
    actual: `${userRole} / permissions: ${
      userPermissions.join(", ") || "-"
    } / adviser: ${adviserStatus.join(", ") || "-"} / admin: ${
      adminStatus.join(", ") || "-"
    }`,
  });

  addCondition({
    section: "LOA Tracker & Analysis",
    tool: "ProCeding",
    allowed:
      userRole === "Staff" &&
      hasPermission("Compliance", "Compliance Support", "Paraplanning", "LOA"),
    reason:
      "Staff with Compliance, Compliance Support, Paraplanning or LOA permission",
    expected: "Staff + Compliance / Compliance Support / Paraplanning / LOA",
    actual: `${userRole} / permissions: ${userPermissions.join(", ") || "-"}`,
  });

  addCondition({
    section: "LOA Tracker & Analysis",
    tool: "Provider Index",
    allowed: userRole === "Staff" && hasPermission("Administrator", "Prod"),
    reason: "Staff with Administrator or Prod permission",
    expected: "Staff + Administrator / Prod",
    actual: `${userRole} / permissions: ${userPermissions.join(", ") || "-"}`,
  });

  addCondition({
    section: "Paraplanning Support",
    tool: "Paraplanning Support",
    allowed:
      userRole === "Staff" &&
      hasPermission("Paraplanning", "Compliance", "Compliance Support"),
    reason: "Staff with Paraplanning, Compliance or Compliance Support",
    expected: "Staff + Paraplanning / Compliance / Compliance Support",
    actual: `${userRole} / permissions: ${userPermissions.join(", ") || "-"}`,
  });

  addCondition({
    section: "Business Summary",
    tool: "Business Summary",
    allowed: canAccessMyPayments(userRole, userPermissions),
    reason:
      "Adviser or Administrator with Can View Payments permission OR Staff with Administrator, Tech, or Finance permission",
    expected: "Advisers or Administrators + Can View Payments / Staff + Administrator, Tech, or Finance",
    actual: `${userRole} / permissions: ${userPermissions.join(", ") || "-"}`,
  });

  addCondition({
    section: "Business Summary",
    tool: "Group Payments",
    allowed: canViewMyPaymentsGroup(userRole, userPermissions),
    reason: "Adviser or Administrator with Can View Group Payments permission",
    expected: "Advisers or Administrators + Can View Group Payments",
    actual: `${userRole} / permissions: ${userPermissions.join(", ") || "-"}`,
  });

  addCondition({
    section: "Marketing & Promotions",
    tool: "Financial Promotions",
    allowed:
      userRole === "Advisers" ||
      userRole === "Staff" ||
      (userRole === "Administrators" &&
        (hasPermission("NSM") || hasAdminStatus("NSM"))) ||
      (userRole === "External" && hasPermission("NSM")),
    reason:
      "Adviser OR Staff OR Administrator with NSM status/permission OR External with NSM permission",
    expected: "Advisers / Staff / Administrators + NSM / External + NSM",
    actual: `${userRole} / permissions: ${userPermissions.join(", ") || "-"} / admin: ${adminStatus.join(", ") || "-"}`,
  });

  return conditions;
};

const buildAuditUser = async (role, document) => {
  const data = document.data() || {};
  const email = normaliseEmail(data.email || document.id);

  const permissions = getPermissionsFromUser(data);
  const status = getStatusFromUser(data);

  const effectivePermissions =
    role === "External"
      ? Array.from(new Set([...permissions, ...status]))
      : permissions;

  const adviserStatus = role === "Advisers" ? status : [];
  const adminStatus = role === "Administrators" ? status : [];

  const canSeeAML = await canAccessAML({
    userRole: role,
    userPermissions: effectivePermissions,
    userEmail: email,
  });

  const config = getPortalSectionConfig({
    userRole: role,
    userEmail: email,
    userPermissions: effectivePermissions,
    adviserStatus,
    adminStatus,
    canSeeAML,
  });

  const visibleSections = getVisibleSections(config);

  const visibleCards = visibleSections.flatMap((section) =>
    section.cards.map((card) => ({
      section: section.title,
      label: card.label,
      route: card.route,
    })),
  );

  return {
    userId: document.id,
    role,
    email,
    name: getUserName({ ...data, userId: document.id }),
    permissions: effectivePermissions,
    status,
    adviserStatus,
    adminStatus,
    relationships: getRelationshipText(data, role),
    canSeeAML,
    visibleSections,
    visibleCards,
    conditions: [
      {
        section: "check²",
        tool: "check²",
        allowed: canSeeAML,
        reason: "Uses canAccessAML() permission logic",
        expected: "Depends on canAccessAML rules",
        actual: `${role} / permissions: ${
          effectivePermissions.join(", ") || "-"
        } / email: ${email}`,
      },
      ...getConditionBreakdown({
        userRole: role,
        userPermissions: effectivePermissions,
        adviserStatus,
        adminStatus,
      }),
    ],
    raw: data,
  };
};

const PortalAccessAuditPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedAccess, setSelectedAccess] = useState("All");
  const [search, setSearch] = useState("");
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAuditUsers = async () => {
    setLoading(true);

    try {
      const snapshots = await Promise.all(
        ROLE_PATHS.map(async ({ role, path }) => {
          const snap = await getDocs(collection(firestore, ...path));

          const docs =
            role === "Advisers"
              ? snap.docs.filter(
                  (document) => {
                    const adviser = document.data() || {};

                    return (
                      adviser.isAppointed !== false ||
                      adviser.isAccessAllowed === true
                    );
                  },
                )
              : snap.docs;

          return {
            role,
            docs,
          };
        }),
      );

      const allUsersNested = await Promise.all(
        snapshots.map(async ({ role, docs }) => {
          const builtUsers = await Promise.all(
            docs.map((document) => buildAuditUser(role, document)),
          );

          return builtUsers;
        }),
      );

      const allUsers = allUsersNested.flat();

      setUsers(allUsers);

      // console.group("[Portal Access Audit]");
      // console.log("Fetched users", {
      //   total: allUsers.length,
      //   advisers: allUsers.filter((user) => user.role === "Advisers").length,
      //   administrators: allUsers.filter((user) => user.role === "Administrators")
      //     .length,
      //   staff: allUsers.filter((user) => user.role === "Staff").length,
      //   external: allUsers.filter((user) => user.role === "External").length,
      // });

      // console.table(
      //   allUsers.map((user) => ({
      //     role: user.role,
      //     name: user.name,
      //     email: user.email,
      //     status: user.status.join(", "),
      //     permissions: user.permissions.join(", "),
      //     sections: user.visibleSections
      //       .map((section) => section.title)
      //       .join(", "),
      //     tools: user.visibleCards.map((card) => card.label).join(", "),
      //   })),
      // );

      // console.groupEnd();
    } catch (error) {
      // console.error("[Portal Access Audit] Failed", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditUsers();
  }, []);

  const accessOptions = useMemo(() => {
    const options = new Set(["All"]);

    users.forEach((user) => {
      user.visibleSections.forEach((section) => options.add(section.title));
      user.visibleCards.forEach((card) => options.add(card.label));
    });

    return Array.from(options);
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesRole = selectedRole === "All" || user.role === selectedRole;

      const matchesAccess =
        selectedAccess === "All" ||
        user.visibleSections.some(
          (section) => section.title === selectedAccess,
        ) ||
        user.visibleCards.some((card) => card.label === selectedAccess);

      const searchText = [
        user.name,
        user.email,
        user.role,
        user.status.join(" "),
        user.permissions.join(" "),
        user.visibleSections.map((section) => section.title).join(" "),
        user.visibleCards.map((card) => card.label).join(" "),
        user.relationships,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchesSearch = !query || searchText.includes(query);

      return matchesRole && matchesAccess && matchesSearch;
    });
  }, [users, selectedRole, selectedAccess, search]);

  const summary = useMemo(() => {
    return {
      total: filteredUsers.length,
      advisers: filteredUsers.filter((user) => user.role === "Advisers").length,
      administrators: filteredUsers.filter(
        (user) => user.role === "Administrators",
      ).length,
      staff: filteredUsers.filter((user) => user.role === "Staff").length,
      external: filteredUsers.filter((user) => user.role === "External").length,
    };
  }, [filteredUsers]);

  const breadcrumbItems = [
    { label: "Portal Administration", to: "/portal/portal-administration" },
    { label: "Access Audit" },
  ];

  return (
    <div className="Main-Page">
      <Helmet>
        <title>Portal Access Audit</title>
        <meta
          name="description"
          content="View adviser portal access conditions by user."
        />
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="portal-access-audit-page">
          <Breadcrumb items={breadcrumbItems} />

          <div className="portal-access-audit-hero">
            <div>
              <p className="portal-access-audit-eyebrow">Admin View</p>
              <h1>Portal Access</h1>
              <p>
                Review which Advisers, Administrators, Staff and External users
                can see each portal section.
              </p>
            </div>

            {/* <button type="button" onClick={fetchAuditUsers}>
              Refresh
            </button> */}
          </div>

          <div className="portal-access-summary-grid">
            <div className="portal-access-summary-card">
              <span>Total</span>
              <strong>{summary.total}</strong>
            </div>
            <div className="portal-access-summary-card">
              <span>Advisers</span>
              <strong>{summary.advisers}</strong>
            </div>
            <div className="portal-access-summary-card">
              <span>Administrators</span>
              <strong>{summary.administrators}</strong>
            </div>
            <div className="portal-access-summary-card">
              <span>Staff</span>
              <strong>{summary.staff}</strong>
            </div>
            <div className="portal-access-summary-card">
              <span>External</span>
              <strong>{summary.external}</strong>
            </div>
          </div>

          <div className="portal-access-filters">
            <div className="portal-access-search-field">
              <span className="portal-access-select-label">Search</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name, email, permission or status"
              />
            </div>

            <PortalAccessSelectField
              label="Role"
              value={selectedRole}
              onChange={setSelectedRole}
              clearable={false}
              searchable={false}
              options={[
                { value: "All", label: "All roles" },
                { value: "Advisers", label: "Advisers" },
                { value: "Administrators", label: "Administrators" },
                { value: "Staff", label: "Staff" },
                { value: "External", label: "External" },
              ]}
            />

            <PortalAccessSelectField
              label="Access"
              value={selectedAccess}
              onChange={setSelectedAccess}
              clearable={false}
              searchable
              searchPlaceholder="Search access..."
              options={accessOptions.map((option) => ({
                value: option,
                label: option === "All" ? "All access" : option,
              }))}
            />
          </div>

          {loading ? (
            <div className="portal-access-empty">Loading access data...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="portal-access-empty">No users found.</div>
          ) : (
            <div className="portal-access-table-wrap">
              <table className="portal-access-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    {/* <th>Status</th> */}
                    {/* <th>Effective Permissions</th> */}
                    <th>Visible Sections</th>
                    <th>Visible Tools</th>
                    {/* <th /> */}
                  </tr>
                </thead>

                <tbody>
                  {filteredUsers.map((user) => {
                    const rowKey = `${user.role}-${user.userId}`;
                    const isExpanded = expandedUserId === rowKey;

                    return (
                      <React.Fragment key={rowKey}>
                        <tr>
                          <td>
                            <div className="portal-access-user">
                              <strong>{user.name}</strong>
                              <span>{user.email}</span>
                            </div>
                          </td>

                          <td>
                            <span className="portal-access-role-pill">
                              {user.role}
                            </span>
                          </td>

                          {/* <td>{user.status.join(", ") || "-"}</td> */}

                          {/* <td>{user.permissions.join(", ") || "-"}</td> */}

                          <td>
                            {user.visibleSections.length ? (
                              <div className="portal-access-pill-wrap">
                                {user.visibleSections.map((section) => (
                                  <span key={section.key}>{section.title}</span>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>

                          <td>
                            {user.visibleCards.length ? (
                              <div className="portal-access-pill-wrap">
                                {user.visibleCards.map((card) => (
                                  <span key={`${card.section}-${card.label}`}>
                                    {card.label}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              "-"
                            )}
                          </td>

                          {/* <td>
                            <button
                              type="button"
                              className="portal-access-details-btn"
                              onClick={() =>
                                setExpandedUserId(isExpanded ? null : rowKey)
                              }
                            >
                              {isExpanded ? "Hide" : "Details"}
                            </button>
                          </td> */}
                        </tr>

                        {isExpanded && (
                          <tr>
                            <td colSpan={5}>
                              <div className="portal-access-details">
                                <div>
                                  <h3>Relationship Data</h3>
                                  <p>
                                    {user.relationships ||
                                      "No adviser/admin relationships found."}
                                  </p>
                                </div>

                                <div>
                                  <h3>Condition Breakdown</h3>

                                  <table className="portal-access-condition-table">
                                    <thead>
                                      <tr>
                                        <th>Section</th>
                                        <th>Tool</th>
                                        <th>Access</th>
                                        <th>Condition</th>
                                        <th>User Values</th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {user.conditions.map((condition) => (
                                        <tr
                                          key={`${condition.section}-${condition.tool}`}
                                        >
                                          <td>{condition.section}</td>
                                          <td>{condition.tool}</td>
                                          <td>
                                            <span
                                              className={
                                                condition.allowed
                                                  ? "portal-access-allowed"
                                                  : "portal-access-denied"
                                              }
                                            >
                                              {condition.allowed
                                                ? "Allowed"
                                                : "Denied"}
                                            </span>
                                          </td>
                                          <td>{condition.reason}</td>
                                          <td>{condition.actual}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortalAccessAuditPage;
